import { authorsfetchCard } from "./authors.js";
import { adsenseAddResponsiveRowAndPush } from "./adsense.js";
import { labelsGetDisplayLabel, labelsHydratePosterLabels } from "./labels.js";

const COMPACT_AD_INTERVAL = 6;
const LISTING_VIEW_STORAGE_KEY = "cartells:listingView";
const IMPORTANT_LABELS = [
  "Música", "Concert", "Concerts", "Teatre", "Espectacle", "Fira", "Mercat",
  "Festival", "Familiar", "Activitats familiars", "Infantil", "Cinema", "Dansa",
  "Esport", "Natura", "Gastronomia", "Cultura", "Patrimoni", "Taller", "Exposició"
];

function compactListGetStoredView() {
  try {
    return sessionStorage.getItem(LISTING_VIEW_STORAGE_KEY) === "list"
      ? "list"
      : "posters";
  } catch (error) {
    return "posters";
  }
}

function compactListStoreView(view) {
  try {
    if (view === "list") {
      sessionStorage.setItem(LISTING_VIEW_STORAGE_KEY, "list");
    } else {
      sessionStorage.removeItem(LISTING_VIEW_STORAGE_KEY);
    }
  } catch (error) {
    // Keep the default poster view when storage is unavailable.
  }
}

function compactListPosterLanguage(poster) {
  return poster.contentLanguage === "es" ? "es" : "ca";
}

function compactListLocalizedPath(path, language) {
  return "/" + language + path;
}

function compactListPosterHref(poster) {
  return compactListLocalizedPath(
    "/" + encodeURIComponent(poster.id) + ".html?id=" + poster.publisherId,
    compactListPosterLanguage(poster)
  );
}

function compactListParseDate(value) {
  const parts = typeof value === "string" ? value.split("/") : [];
  if (parts.length !== 3) {
    return null;
  }
  return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
}

function compactListDateParts(beginValue, endValue, language) {
  const begin = compactListParseDate(beginValue);
  const end = compactListParseDate(endValue) || begin;
  if (!begin || !end) {
    return {weekday: "", primary: "", primaryEnd: "", month: "", period: "", weekend: false, range: false, crossMonth: false};
  }
  const locale = language === "es" ? "es-ES" : "ca-ES";
  const sameDay = begin.getTime() === end.getTime();
  const sameMonth = begin.getFullYear() === end.getFullYear()
    && begin.getMonth() === end.getMonth();
  const currentYear = new Date().getFullYear();
  const showYear = begin.getFullYear() !== currentYear || end.getFullYear() !== currentYear
    || begin.getFullYear() !== end.getFullYear();
  const month = (date) => new Intl.DateTimeFormat(locale, {month: "short"})
    .format(date).replace(".", "").toLocaleUpperCase(locale);
  const shortDate = (date) => String(date.getDate()) + " " + month(date)
    + (showYear ? " " + String(date.getFullYear()).slice(-2) : "");
  const fullDate = (date) => new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: showYear ? "numeric" : undefined
  }).format(date);
  const weekend = (begin.getDay() === 0 || begin.getDay() === 6)
    && (sameDay || ((end.getTime() - begin.getTime()) / 86400000 <= 1
      && (end.getDay() === 0 || end.getDay() === 6)));

  return {
    weekday: sameDay
      ? new Intl.DateTimeFormat(locale, {weekday: "short"})
        .format(begin).replace(".", "").toLocaleUpperCase(locale)
      : "",
    primary: sameDay
      ? String(begin.getDate())
      : (sameMonth ? String(begin.getDate()) : shortDate(begin)),
    primaryEnd: sameDay ? "" : (sameMonth ? String(end.getDate()) : shortDate(end)),
    month: sameDay || sameMonth ? month(begin) + (showYear ? " " + begin.getFullYear() : "") : "",
    period: sameDay ? "" : (language === "es" ? "Del " : "Del ")
      + fullDate(begin) + (language === "es" ? " al " : " al ") + fullDate(end),
    weekend,
    range: sameDay === false,
    crossMonth: sameDay === false && sameMonth === false
  };
}

function compactListCreateChip(value, language, className = "") {
  const chip = document.createElement("span");
  chip.className = "listingCompactListChip" + (className ? " " + className : "");
  chip.dataset.labelValue = value;
  chip.dataset.labelLanguage = language;
  chip.textContent = labelsGetDisplayLabel(value, language);
  return chip;
}

function compactListSelectLabels(poster, author) {
  const values = Array.isArray(poster.labelsList) ? poster.labelsList : [];
  const selected = [];
  const seen = new Set();
  const add = (value, type) => {
    if (typeof value !== "string" || value.trim() === "" || seen.has(value) || selected.length >= 3) {
      return;
    }
    seen.add(value);
    selected.push({value, type});
  };

  if (author && typeof author.space === "string" && author.space.trim() !== "") {
    add(author.space, "space");
    const superLabels = new Set(Array.isArray(author.superLabel) ? author.superLabel : []);
    values.filter((value) => superLabels.has(value)).forEach((value) => add(value, "super"));
  }
  add(poster.town, "town");
  IMPORTANT_LABELS.filter((value) => values.includes(value)).forEach((value) => add(value, "topic"));
  values.forEach((value) => add(value, "topic"));
  return selected;
}

function compactListRenderLabels(wrapper, poster, author) {
  const language = compactListPosterLanguage(poster);
  wrapper.replaceChildren();
  compactListSelectLabels(poster, author).forEach(({value, type}) => {
    const chip = compactListCreateChip(
      value,
      language,
      type === "space" || type === "super" ? "listingCompactListChip--space" : ""
    );
    wrapper.appendChild(chip);
  });
  labelsHydratePosterLabels(wrapper);
}

function compactListCreateRow(poster) {
  const language = compactListPosterLanguage(poster);
  const date = compactListDateParts(poster.dateBegin, poster.dateEnd, language);
  const row = document.createElement("article");
  row.className = "listingCompactListRow";
  row.lang = language;

  const link = document.createElement("a");
  link.className = "listingCompactListLink";
  link.href = compactListPosterHref(poster);
  link.setAttribute("aria-label", poster.title || "");

  const dateBlock = document.createElement("span");
  dateBlock.className = "listingCompactListDate"
    + (date.weekend ? " listingCompactListDate--weekend" : "")
    + (date.range ? " listingCompactListDate--range" : "")
    + (date.crossMonth ? " listingCompactListDate--cross-month" : "");
  const weekday = document.createElement("span");
  weekday.className = "listingCompactListWeekday";
  weekday.textContent = date.weekday;
  const day = document.createElement("span");
  day.className = "listingCompactListDay";
  const dayStart = document.createElement("span");
  dayStart.textContent = date.primary;
  day.appendChild(dayStart);
  if (date.primaryEnd) {
    const dayEnd = document.createElement("span");
    dayEnd.textContent = date.primaryEnd;
    day.appendChild(dayEnd);
  }
  const month = document.createElement("span");
  month.className = "listingCompactListMonth";
  month.textContent = date.month;
  dateBlock.append(weekday, day, month);

  const content = document.createElement("span");
  content.className = "listingCompactListContent";
  const title = document.createElement("span");
  title.className = "listingCompactListTitle";
  title.textContent = poster.title || "";
  const subtitle = document.createElement("span");
  subtitle.className = "listingCompactListSubtitle";
  subtitle.textContent = poster.subtitle || "";
  const period = document.createElement("span");
  period.className = "listingCompactListPeriod";
  period.textContent = date.period;
  const labels = document.createElement("span");
  labels.className = "listingCompactListLabels";
  compactListRenderLabels(labels, poster, null);
  content.append(title, subtitle);
  if (date.period) {
    content.appendChild(period);
  }
  content.appendChild(labels);

  const arrow = document.createElement("span");
  arrow.className = "listingCompactListArrow";
  arrow.setAttribute("aria-hidden", "true");
  arrow.textContent = "›";
  link.append(dateBlock, content, arrow);
  row.appendChild(link);

  authorsfetchCard(poster.publisherId).then((author) => {
    if (row.isConnected) {
      compactListRenderLabels(labels, poster, author);
    }
  }).catch(() => {});
  return row;
}

function compactListMadridToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return values.year + "-" + values.month + "-" + values.day;
}

function compactListDateFilterStartUp(selector) {
  const input = selector.querySelector(".listingDateFilterInput");
  if (!input) {
    return;
  }
  const url = new URL(window.location.href);
  const requestedDate = url.searchParams.get("from");
  input.value = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate || "")
    ? requestedDate
    : compactListMadridToday();
  input.addEventListener("change", () => {
    if (!input.value) {
      return;
    }
    url.searchParams.set("from", input.value);
    const selectedView = selector.querySelector(
      "[data-listing-view][aria-pressed='true']"
    )?.dataset.listingView;
    if (selectedView === "list") {
      url.searchParams.set("view", "list");
    } else {
      url.searchParams.delete("view");
    }
    ["index", "cursor", "otherIndex", "otherCursor"].forEach((name) => url.searchParams.delete(name));
    window.location.assign(url.pathname + "?" + url.searchParams.toString());
  });
}

function compactListStartUp() {
  const selectors = Array.from(document.querySelectorAll(".listingViewSelector"));
  if (selectors.length === 0) {
    return;
  }

  selectors.forEach((selector) => compactListSelectorStartUp(selector, selectors));
}

function compactListSelectorStartUp(selector, selectors) {
  if (selector.dataset.listingViewInitialized === "true") {
    return;
  }
  const postersWrapper = document.getElementById(
    selector.dataset.postersWrapper || "mainPostersWrapper"
  );
  const listWrapper = document.getElementById(
    selector.dataset.listWrapper || "mainCompactListWrapper"
  );
  if (!postersWrapper || !listWrapper) {
    return;
  }
  selector.dataset.listingViewInitialized = "true";
  compactListDateFilterStartUp(selector);

  const targets = [{
    postersWrapper,
    listWrapper,
    rendered: new WeakSet(),
    renderedCount: 0
  }];
  const otherPostersWrapper = document.getElementById("mainOtherPostersWrapper");
  const otherListWrapper = document.getElementById("mainOtherCompactListWrapper");
  const otherListingHasOwnSelector = selectors.some((candidate) =>
    candidate !== selector &&
    candidate.dataset.postersWrapper === "mainOtherPostersWrapper" &&
    candidate.dataset.listWrapper === "mainOtherCompactListWrapper"
  );
  if (!selector.dataset.postersWrapper && !otherListingHasOwnSelector &&
      otherPostersWrapper && otherListWrapper && otherPostersWrapper !== postersWrapper) {
    targets.push({
      postersWrapper: otherPostersWrapper,
      listWrapper: otherListWrapper,
      rendered: new WeakSet(),
      renderedCount: 0
    });
  }
  let activeView = "posters";

  const appendListingAd = (target, trailing = false) => {
    const ad = document.createElement("div");
    ad.className = "listingCompactListAd";
    if (trailing) {
      ad.dataset.listingAdTrailing = "true";
    }
    target.listWrapper.appendChild(ad);
  };

  const appendPoster = (target, posterElement) => {
    const poster = posterElement._posterData;
    if (!poster || target.rendered.has(posterElement)) {
      return;
    }
    target.rendered.add(posterElement);
    target.listWrapper.appendChild(compactListCreateRow(poster));
    target.renderedCount++;
    if (target.renderedCount % COMPACT_AD_INTERVAL === 0) {
      appendListingAd(target);
    }
  };

  const syncTrailingAd = (target) => {
    const trailingAd = target.listWrapper.querySelector(
      ".listingCompactListAd[data-listing-ad-trailing='true']"
    );
    const shouldAppend = target.postersWrapper.dataset.postersLoaded === "true"
      && target.renderedCount > 0
      && target.renderedCount < COMPACT_AD_INTERVAL;

    if (!shouldAppend) {
      trailingAd?.remove();
    } else if (!trailingAd) {
      appendListingAd(target, true);
    }
  };

  const activateVisibleListAds = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (activeView !== "list") {
          return;
        }
        targets.forEach((target) => {
          if (target.listWrapper.hidden || target.listWrapper.getBoundingClientRect().width <= 0) {
            return;
          }
          target.listWrapper.querySelectorAll(
            ".listingCompactListAd:not([data-listing-ad-initialized])"
          ).forEach((ad) => {
            if (ad.getBoundingClientRect().width <= 0) {
              return;
            }
            ad.dataset.listingAdInitialized = "true";
            adsenseAddResponsiveRowAndPush(ad);
          });
        });
      });
    });
  };

  const sync = (target) => {
    const posters = Array.from(
      target.postersWrapper.querySelectorAll(".posterWrapper")
    ).filter((poster) => !target.rendered.has(poster));
    if (posters.length > 0) {
      target.listWrapper.querySelector(
        ".listingCompactListAd[data-listing-ad-trailing='true']"
      )?.remove();
      posters.forEach((poster) => appendPoster(target, poster));
    }
    syncTrailingAd(target);
  };

  const applyView = (view) => {
      activeView = view === "list" ? "list" : "posters";
      selector.querySelectorAll("[data-listing-view]").forEach((option) => {
        const active = option.dataset.listingView === activeView;
        option.classList.toggle("is-active", active);
        option.setAttribute("aria-pressed", String(active));
      });
      if (activeView === "list") {
        targets.forEach((target) => {
          sync(target);
          target.postersWrapper.hidden = true;
          target.listWrapper.hidden = false;
        });
        activateVisibleListAds();
      } else {
        targets.forEach((target) => {
          target.listWrapper.hidden = true;
          target.postersWrapper.hidden = false;
        });
      }
  };

  selector.querySelectorAll("[data-listing-view]").forEach((button) => {
    button.addEventListener("click", () => {
      compactListStoreView(button.dataset.listingView);
      applyView(button.dataset.listingView);
    });
  });

  targets.forEach((target) => {
    new MutationObserver(() => {
      sync(target);
      activateVisibleListAds();
    }).observe(target.postersWrapper, {
      childList: true,
      attributes: true,
      attributeFilter: ["data-posters-loaded"]
    });
    if ("ResizeObserver" in window) {
      new ResizeObserver(() => {
        activateVisibleListAds();
      }).observe(target.listWrapper);
    }
  });

  const requestedView = new URL(window.location.href).searchParams.get("view");
  if (requestedView === "list") {
    compactListStoreView("list");
    applyView("list");
  } else if (requestedView === "posters") {
    compactListStoreView("posters");
    applyView("posters");
  } else {
    applyView(compactListGetStoredView());
  }
}

export { compactListStartUp };
